'use client'

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'
import { isNil } from 'ramda'

import { CartResponseTypes, useCartStore } from 'modules/cart'
import { extractTokenFromClientSession } from 'modules/customer'
import { apiService } from 'shared/api'
import { isClient } from 'shared/utils/common'

const api = apiService()

const CACHE_TIME_MS = 5 * 60 * 1000

const StoreInitializer = () => {
  const { init: initCart, cart, setCart } = useCartStore()
  const session = useSession()
  const [isClientMounted, setIsClientMounted] = useState(false)

  // Ensure we're on the client side and fully hydrated
  useEffect(() => {
    setIsClientMounted(true)
  }, [])

  const cartQuery = useQuery({
    queryKey: ['cart', cart?.id ?? ''],
    queryFn: async () => {
      const token = extractTokenFromClientSession(session)

      if (!cart?.id) {
        return null
      }

      try {
        const result: CartResponseTypes = await api.get(
          `/cart/${cart.id}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        )

        if (result) {
          const cartValues = result?.data?.data || null

          if (isNil(cartValues?.items)) {
            cartValues.items = []
          }
          setCart(cartValues)

          return cartValues
        }

        return null
      } catch (err) {
        console.error('🛒 Cart Query Error:', {
          error: err,
          cartId: cart?.id,
          hasToken: !!token,
          sessionStatus: session.status
        })

        return null
      }
    },
    enabled: isClientMounted && !!cart?.id && session.status !== 'loading',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 60 * 1000, // 1 minute
    gcTime: isClient() ? CACHE_TIME_MS : Infinity,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  })

  useEffect(() => {
    initCart(session)
  }, [initCart, session])

  useEffect(() => {
    const handleFocus = () => {
      if (cartQuery.refetch) {
        void cartQuery.refetch()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (cartQuery.refetch) {
          void cartQuery.refetch()
        }
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [cartQuery.refetch])

  return null
}

export default StoreInitializer
